/**
 * Accès à l'interface de programmation.
 *
 * Les erreurs remontées par le serveur suivent le format « problem detail » :
 * le champ `detail` porte un message déjà rédigé pour un comptable — « Pièce
 * déséquilibrée : débit 1 200,00 MAD, crédit 1 100,00 MAD ». Il est repris tel
 * quel plutôt que remplacé par un message générique.
 */

import type { ChampImporte } from "../domaine/importation";
import type { ChampDeReleve } from "../domaine/banque";

export class ErreurApi extends Error {
  // Champs déclarés puis affectés : « erasableSyntaxOnly » interdit les
  // propriétés de constructeur, qui ne sont pas effaçables à la compilation.
  readonly statut: number;
  readonly titre: string;

  constructor(statut: number, titre: string, detail: string) {
    super(detail);
    this.name = "ErreurApi";
    this.statut = statut;
    this.titre = titre;
  }
}

interface ProblemDetail {
  title?: string;
  detail?: string;
}

async function appeler<T>(chemin: string, init?: RequestInit): Promise<T> {
  const reponse = await fetch(`/api${chemin}`, {
    // Le cookie de session est « HttpOnly » : le code ne le lit pas, mais doit
    // demander explicitement au navigateur de le joindre.
    credentials: "same-origin",
    headers: { Accept: "application/json", ...(init?.headers ?? {}) },
    ...init,
  });

  if (!reponse.ok) {
    let titre = reponse.statusText;
    let detail = `Le serveur a répondu ${reponse.status}.`;
    try {
      const probleme = (await reponse.json()) as ProblemDetail;
      titre = probleme.title ?? titre;
      detail = probleme.detail ?? detail;
    } catch {
      // La réponse n'était pas du JSON : les valeurs par défaut suffisent.
    }
    throw new ErreurApi(reponse.status, titre, detail);
  }

  // Une suppression répond sans corps : il n'y a rien à lire.
  if (reponse.status === 204) return undefined as T;

  return (await reponse.json()) as T;
}

// ---------------------------------------------------------------------------
// Référentiels
// ---------------------------------------------------------------------------

export interface Classe {
  chiffre: number;
  libelle: string;
  role: string;
}

export interface Referentiel {
  id: string;
  code: string;
  libelle: string;
  pays: string;
  version: string;
  dateEffet: string;
  nombreDeComptes: number;
  classes: Classe[];
}

export type Sens = "DEBIT" | "CREDIT";

export interface Compte {
  numero: string;
  libelle: string;
  classe: number;
  niveau: number;
  sensNormal: Sens;
  mouvementable: boolean;
  lettrable: boolean;
  rapprochable: boolean;
  auxiliarisable: boolean;
  collectif: boolean;
  /** Code du taux de TVA proposé d'office à la saisie ; la valeur se lit dans le barème à la date de la pièce. */
  codeDeTva: string | null;
  /** Vrai pour un sous-compte ouvert par le dossier (EF-0204). */
  propre?: boolean;
}

export interface PlanDuDossierVue {
  referentiel: string;
  version: string;
  date: string;
  comptes: Compte[];
  nombreDeSousComptes: number;
  /** Sous-comptes du dossier que la version en vigueur du référentiel a rendus caducs. */
  ecartes: { numero: string; libelle: string; motif: string }[];
}

export interface SousCompteOuvertVue {
  compte: string;
  libelle: string;
  parentDejaMouvemente: boolean;
}

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

export interface Utilisateur {
  id: string;
  nom: string;
  courriel: string;
}

export interface TenantVue {
  id: string;
  raisonSociale: string;
  type: string;
  pays: string;
}

export interface SessionOuverte {
  utilisateur: Utilisateur;
  tenant: TenantVue;
  tenantsAccessibles: TenantVue[];
  roles: string[];
  permissions: string[];
  accedeATousLesDossiers: boolean;
  dossiersAffectes: string[];
  /** Vrai si l'utilisateur a enrôlé un second facteur (EF-0103). */
  secondFacteurActif: boolean;
  /** Vrai si cette session a été ouverte avec son code. */
  secondFacteurVerifie: boolean;
  /** Permissions accordées mais retenues faute de second facteur. */
  permissionsRetenues: string[];
}

/** Premier temps d'une connexion avec second facteur : le code reste à donner. */
export interface SecondFacteurRequis {
  secondFacteurRequis: true;
  preuve: string;
}

export const api = {
  session: () => appeler<SessionOuverte>("/session"),

  ouvrirSession: (courriel: string, motDePasse: string) =>
    appeler<SessionOuverte | SecondFacteurRequis>("/session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ courriel, motDePasse }),
    }),

  /** Second temps : la preuve du mot de passe et le code de l'application ouvrent la session. */
  ouvrirAvecLeCode: (preuve: string, code: string) =>
    appeler<SessionOuverte>("/session/second-facteur", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ preuve, code }),
    }),

  enrolerLeSecondFacteur: () =>
    appeler<{ secret: string; adresse: string }>("/session/second-facteur/enrolement", { method: "POST" }),

  activerLeSecondFacteur: (code: string) =>
    appeler<SessionOuverte>("/session/second-facteur/activation", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    }),

  changerDeTenant: (tenant: string) =>
    appeler<SessionOuverte>("/session/tenant", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenant }),
    }),

  fermerSession: async () => {
    await fetch("/api/session", { method: "DELETE", credentials: "same-origin" });
  },

  referentiels: () => appeler<Referentiel[]>("/referentiels"),

  referentiel: (code: string) => appeler<Referentiel>(`/referentiels/${code}`),

  comptes: (code: string, options: { recherche?: string; classe?: number; limite?: number } = {}) => {
    const parametres = new URLSearchParams();
    if (options.recherche) parametres.set("recherche", options.recherche);
    if (options.classe !== undefined) parametres.set("classe", String(options.classe));
    if (options.limite !== undefined) parametres.set("limite", String(options.limite));
    const requete = parametres.toString();
    return appeler<Compte[]>(`/referentiels/${code}/comptes${requete ? `?${requete}` : ""}`);
  },
};

/** Plan d'un dossier : le référentiel augmenté de ses sous-comptes (EF-0204). */
export const planDuDossierApi = {
  plan: (dossier: string, date?: string) =>
    appeler<PlanDuDossierVue>(`/dossiers/${dossier}/plan${date ? `?date=${date}` : ""}`),

  ouvrir: (dossier: string, sousCompte: { numero: string; libelle: string; codeDeTva: string | null }) =>
    appeler<SousCompteOuvertVue>(`/dossiers/${dossier}/sous-comptes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(sousCompte),
    }),

  modifier: (dossier: string, numero: string, modification: { libelle: string; codeDeTva: string | null }) =>
    appeler<SousCompteOuvertVue>(`/dossiers/${dossier}/sous-comptes/${numero}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(modification),
    }),

  supprimer: (dossier: string, numero: string) =>
    appeler<void>(`/dossiers/${dossier}/sous-comptes/${numero}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Dossiers et exercices
// ---------------------------------------------------------------------------

export interface Identifiants {
  pays: string;
  principal: string;
  ice: string | null;
  identifiantFiscal: string | null;
  registreDeCommerce: string | null;
  taxeProfessionnelle: string | null;
  affiliationCnss: string | null;
  siren: string | null;
  siret: string | null;
  tvaIntracommunautaire: string | null;
  codeApe: string | null;
}

export interface ParametresTva {
  assujetti: boolean;
  periodicite: string | null;
  regime: string | null;
  prorataDeDeduction: string | null;
  deductionIntegrale: boolean;
}

export interface DossierVue {
  id: string;
  raisonSociale: string;
  formeJuridique: string | null;
  pays: string;
  identifiants: Identifiants;
  codeDuReferentiel: string;
  devise: string;
  regimeDImposition: string;
  regimeDImpositionLibelle: string;
  tva: ParametresTva;
  statut: string;
}

export interface ExerciceVue {
  id: string;
  dossier: string;
  libelle: string;
  debut: string;
  fin: string;
  dureeEnMois: number;
  statut: string;
  statutLibelle: string;
  verrouilleJusquAu: string | null;
  accepteLaSaisie: boolean;
  transitionsPossibles: string[];
}

export interface Option {
  code: string;
  libelle: string;
}

export interface OptionsDeDossier {
  regimesDImposition: Record<string, Option[]>;
  periodicitesDeTva: Option[];
  regimesDeTva: Option[];
}

export interface CreationDeDossier {
  raisonSociale: string;
  formeJuridique?: string | null;
  pays: string;
  ice?: string | null;
  identifiantFiscal?: string | null;
  registreDeCommerce?: string | null;
  taxeProfessionnelle?: string | null;
  affiliationCnss?: string | null;
  siren?: string | null;
  siret?: string | null;
  tvaIntracommunautaire?: string | null;
  codeApe?: string | null;
  codeDuReferentiel: string;
  devise: string;
  regimeDImposition: string;
  tvaAssujetti: boolean;
  tvaPeriodicite?: string | null;
  tvaRegime?: string | null;
  tvaProrata?: string | null;
}

const envoyer = <T,>(chemin: string, corps: unknown) =>
  appeler<T>(chemin, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(corps),
  });

export const dossiersApi = {
  lister: () => appeler<DossierVue[]>("/dossiers"),

  consulter: (id: string) => appeler<DossierVue>(`/dossiers/${id}`),

  ouvrir: (demande: CreationDeDossier) => envoyer<DossierVue>("/dossiers", demande),

  archiver: (id: string) => envoyer<DossierVue>(`/dossiers/${id}/archivage`, {}),

  options: () => appeler<OptionsDeDossier>("/dossiers-options"),

  exercices: (dossier: string) => appeler<ExerciceVue[]>(`/dossiers/${dossier}/exercices`),

  ouvrirUnExercice: (dossier: string, debut: string, fin: string) =>
    envoyer<ExerciceVue>(`/dossiers/${dossier}/exercices`, { debut, fin }),

  changerLeStatut: (exercice: string, statut: string, motif?: string) =>
    envoyer<ExerciceVue>(`/exercices/${exercice}/statut`, { statut, motif: motif ?? null }),

  verrouiller: (exercice: string, jusquAu: string) =>
    envoyer<ExerciceVue>(`/exercices/${exercice}/verrou`, { jusquAu }),
};

// ---------------------------------------------------------------------------
// Journaux, pièces et livres
// ---------------------------------------------------------------------------

export type StatutPiece = "BROUILLARD" | "VALIDEE";

export interface JournalVue {
  code: string;
  libelle: string;
  type: string;
  typeLibelle: string;
  compteDeContrepartie: string | null;
  accepteLaSaisieManuelle: boolean;
}

export interface LigneVue {
  rang: number;
  compte: string;
  sens: Sens;
  montant: number;
  debit: number;
  credit: number;
  libelle: string;
  tiers: string | null;
  echeance: string | null;
  codeDeTva: string | null;
}

export interface ValidationVue {
  numero: number;
  le: string;
  par: string;
  rangDeChaine: number;
  empreinte: string;
  empreintePrecedente: string | null;
}

export interface PieceVue {
  id: string;
  dossier: string;
  exercice: string;
  journal: string;
  date: string;
  libelle: string;
  reference: string | null;
  devise: string;
  statut: StatutPiece;
  statutLibelle: string;
  numero: number | null;
  totalDebit: number;
  totalCredit: number;
  pieceContrepassee: string | null;
  estModifiable: boolean;
  estIntegre: boolean;
  validation: ValidationVue | null;
  lignes: LigneVue[];
}

export interface LigneSaisie {
  compte: string;
  sens: Sens;
  montant: number;
  libelle: string;
  tiers?: string | null;
  echeance?: string | null;
  codeDeTva?: string | null;
}

export interface TauxDeTvaVue {
  code: string;
  libelle: string;
  pourCent: number;
  dateEffet: string;
  dateFin: string | null;
  referenceLegale: string;
}

export interface RegleDeTvaVue {
  prefixeDeBase: string;
  nature: string;
  natureLibelle: string;
  compteDeTva: string;
}

/** Barème de TVA du dossier à une date : les taux en vigueur, et où va la taxe de chaque base. */
export interface BaremeVue {
  pays: string;
  date: string;
  taux: TauxDeTvaVue[];
  regles: RegleDeTvaVue[];
}

export interface SaisieDePiece {
  journal: string;
  date: string;
  libelle: string;
  reference?: string | null;
  lignes: LigneSaisie[];
}

export interface MouvementVue {
  piece: string;
  date: string;
  journal: string;
  numero: number | null;
  statut: StatutPiece;
  rang: number;
  libelle: string;
  libelleDePiece: string;
  reference: string | null;
  tiers: string | null;
  echeance: string | null;
  debit: number;
  credit: number;
  soldeCumule: number;
  /** Lettre du lettrage actif qui porte ce mouvement, s'il est lettré. */
  lettre: string | null;
}

export interface CompteDuGrandLivreVue {
  compte: string;
  libelle: string;
  /** Mouvements du compte antérieurs à la page (ENF-03). */
  reportDebit: number;
  reportCredit: number;
  /** Solde reporté en tête de page, signé : positif au débit. */
  report: number;
  commenceAvant: boolean;
  continueApres: boolean;
  mouvements: MouvementVue[];
  /** Totaux du compte entier, toutes pages confondues. */
  totalDebit: number;
  totalCredit: number;
  solde: number;
  sensDuSolde: Sens | null;
  nombreDeMouvements: number;
}

export interface GrandLivreVue {
  devise: string;
  comptes: CompteDuGrandLivreVue[];
  totalDebit: number;
  totalCredit: number;
  nombreDeMouvements: number;
  /** Page lue, à partir de zéro. */
  page: number;
  taille: number;
  nombreDePages: number;
}

export interface SoldeDeCompteVue {
  compte: string;
  libelle: string;
  tiers: string | null;
  debit: number;
  credit: number;
  soldeDebiteur: number;
  soldeCrediteur: number;
}

export interface BalanceVue {
  devise: string;
  lignes: SoldeDeCompteVue[];
  totalDebit: number;
  totalCredit: number;
  totalSoldesDebiteurs: number;
  totalSoldesCrediteurs: number;
  estEquilibree: boolean;
}

export interface RuptureVue {
  piece: string;
  rang: number | null;
  motif: string;
}

export interface RapportDeChaineVue {
  piecesVerifiees: number;
  estIntacte: boolean;
  ruptures: RuptureVue[];
}

const avecParametres = (chemin: string, valeurs: Record<string, string | number | boolean | undefined>) => {
  const parametres = new URLSearchParams();
  for (const [nom, valeur] of Object.entries(valeurs)) {
    if (valeur !== undefined && valeur !== "") parametres.set(nom, String(valeur));
  }
  const requete = parametres.toString();
  return requete ? `${chemin}?${requete}` : chemin;
};

export const ecrituresApi = {
  journaux: (dossier: string) => appeler<JournalVue[]>(`/dossiers/${dossier}/journaux`),

  pieces: (exercice: string, filtres: { journal?: string; statut?: string } = {}) =>
    appeler<PieceVue[]>(avecParametres(`/exercices/${exercice}/pieces`, filtres)),

  consulter: (id: string) => appeler<PieceVue>(`/pieces/${id}`),

  saisir: (dossier: string, saisie: SaisieDePiece) => envoyer<PieceVue>(`/dossiers/${dossier}/pieces`, saisie),

  corriger: (id: string, saisie: SaisieDePiece) =>
    appeler<PieceVue>(`/pieces/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(saisie),
    }),

  supprimer: (id: string) => appeler<void>(`/pieces/${id}`, { method: "DELETE" }),

  valider: (id: string) => envoyer<PieceVue>(`/pieces/${id}/validation`, {}),

  contrepasser: (id: string, date: string, motif: string | null) =>
    envoyer<PieceVue>(`/pieces/${id}/contrepassation`, { date, motif }),

  grandLivre: (
    exercice: string,
    filtres: {
      compte?: string;
      tiers?: string;
      du?: string;
      au?: string;
      brouillard?: boolean;
      page?: number;
      taille?: number;
    } = {},
  ) => appeler<GrandLivreVue>(avecParametres(`/exercices/${exercice}/grand-livre`, filtres)),

  balance: (
    exercice: string,
    filtres: { jusquAu?: string; niveau?: number; parTiers?: boolean; brouillard?: boolean } = {},
  ) => appeler<BalanceVue>(avecParametres(`/exercices/${exercice}/balance`, filtres)),

  chaine: (dossier: string) => appeler<RapportDeChaineVue>(`/dossiers/${dossier}/chaine`),

  /** Tiers déjà nommés dans le dossier, contenant le fragment tapé. */
  tiers: (dossier: string, recherche: string) =>
    appeler<string[]>(avecParametres(`/dossiers/${dossier}/tiers`, { recherche })),

  /** Barème de TVA applicable au dossier à la date d'une pièce (RG-21). */
  bareme: (dossier: string, date: string) =>
    appeler<BaremeVue>(avecParametres(`/dossiers/${dossier}/tva`, { date })),
};

// ---------------------------------------------------------------------------
// Lettrage des comptes de tiers (EF-0407)
// ---------------------------------------------------------------------------

export interface EcritureLettrableVue {
  piece: string;
  rang: number;
  numero: number | null;
  journal: string;
  date: string;
  libelle: string;
  reference: string | null;
  compte: string;
  tiers: string | null;
  sens: Sens;
  montant: number;
  debit: number;
  credit: number;
}

export type StatutDeLettrage = "TOTAL" | "PARTIEL" | "ANNULE";

export interface AnnulationVue {
  le: string;
  par: string;
  motif: string;
}

export interface LettrageVue {
  id: string;
  dossier: string;
  compte: string;
  tiers: string | null;
  lettre: string;
  statut: StatutDeLettrage;
  statutLibelle: string;
  totalDebit: number;
  totalCredit: number;
  /** Débit moins crédit des écritures lettrées : nul pour un lettrage total. */
  solde: number;
  ecritures: EcritureLettrableVue[];
  creeLe: string;
  annulation: AnnulationVue | null;
}

export interface CompteAuxiliaireVue {
  compte: string;
  tiers: string | null;
  nombreDEcritures: number;
  soldeNonLettre: number;
}

export interface SituationDeLettrageVue {
  compte: string;
  tiers: string | null;
  nonLettrees: EcritureLettrableVue[];
  /** Lettrages actifs — totaux et partiels — du compte auxiliaire. */
  lettrages: LettrageVue[];
  soldeNonLettre: number;
}

export interface CleDEcriture {
  piece: string;
  rang: number;
}

export interface ResultatAutomatiqueVue {
  /** Groupes d'écritures que l'automate apparierait, dans l'ordre. */
  propositions: EcritureLettrableVue[][];
  /** Lettrages créés, si l'application a été demandée. */
  lettrages: LettrageVue[];
}

export const lettrageApi = {
  /** Comptes auxiliaires lettrables qui portent des écritures non lettrées. */
  comptes: (dossier: string) => appeler<CompteAuxiliaireVue[]>(`/dossiers/${dossier}/lettrage/comptes`),

  situation: (dossier: string, compte: string, tiers: string | null) =>
    appeler<SituationDeLettrageVue>(
      avecParametres(`/dossiers/${dossier}/lettrage`, { compte, tiers: tiers ?? undefined }),
    ),

  lettrer: (dossier: string, compte: string, tiers: string | null, lignes: CleDEcriture[]) =>
    envoyer<LettrageVue>(`/dossiers/${dossier}/lettrage`, { compte, tiers, lignes }),

  /** Propose (appliquer = false) ou applique (appliquer = true) le lettrage automatique. */
  automatique: (dossier: string, compte: string, tiers: string | null, appliquer: boolean) =>
    envoyer<ResultatAutomatiqueVue>(`/dossiers/${dossier}/lettrage/automatique`, { compte, tiers, appliquer }),

  completer: (id: string, lignes: CleDEcriture[]) => envoyer<LettrageVue>(`/lettrages/${id}/complement`, { lignes }),

  constaterLEcart: (id: string, date: string, libelle: string | null) =>
    envoyer<LettrageVue>(`/lettrages/${id}/ecart`, { date, libelle }),

  annuler: (id: string, motif: string) => envoyer<LettrageVue>(`/lettrages/${id}/annulation`, { motif }),
};

// ---------------------------------------------------------------------------
// Fournisseurs, factures d'achat et échéancier (M05)
// ---------------------------------------------------------------------------

export type ModeDEcheance = "COMPTANT" | "JOURS_NETS" | "JOURS_FIN_DE_MOIS" | "JOURS_FIN_DE_MOIS_LE";

export interface ConditionsVue {
  mode: ModeDEcheance;
  modeLibelle: string;
  delaiEnJours: number;
  jourDePaiement: number | null;
  moyen: string | null;
  moyenLibelle: string | null;
  escomptePourCent: number | null;
  /** Les conditions telles qu'elles se disent : « 60 jours fin de mois ». */
  resume: string;
}

export interface CoordonneesBancairesVue {
  titulaire: string | null;
  banque: string | null;
  rib: string | null;
  iban: string | null;
  swift: string | null;
  permettentUnVirement: boolean;
}

export interface ContactVue {
  adresse: string | null;
  ville: string | null;
  pays: string | null;
  telephone: string | null;
  courriel: string | null;
}

export interface FournisseurVue {
  id: string;
  dossier: string;
  /** Auxiliaire porté par la ligne d'écriture : c'est lui qui relie la fiche à la comptabilité. */
  code: string;
  raisonSociale: string;
  compteDeRattachement: string;
  pays: string | null;
  identifiantPrincipal: string | null;
  ice: string | null;
  identifiantFiscal: string | null;
  registreDeCommerce: string | null;
  taxeProfessionnelle: string | null;
  affiliationCnss: string | null;
  siren: string | null;
  siret: string | null;
  tvaIntracommunautaire: string | null;
  codeApe: string | null;
  conditions: ConditionsVue;
  coordonneesBancaires: CoordonneesBancairesVue | null;
  contact: ContactVue | null;
  statut: "ACTIF" | "ARCHIVE";
  statutLibelle: string;
}

export interface SaisieDeFournisseur {
  code?: string | null;
  raisonSociale: string;
  compteDeRattachement?: string | null;
  pays?: string | null;
  ice?: string | null;
  identifiantFiscal?: string | null;
  registreDeCommerce?: string | null;
  taxeProfessionnelle?: string | null;
  affiliationCnss?: string | null;
  siren?: string | null;
  siret?: string | null;
  tvaIntracommunautaire?: string | null;
  codeApe?: string | null;
  modeDEcheance?: ModeDEcheance | null;
  delaiEnJours?: number | null;
  jourDePaiement?: number | null;
  moyenDeReglement?: string | null;
  escomptePourCent?: number | null;
  titulaireDuCompte?: string | null;
  banque?: string | null;
  rib?: string | null;
  iban?: string | null;
  swift?: string | null;
  adresse?: string | null;
  ville?: string | null;
  paysDuContact?: string | null;
  telephone?: string | null;
  courriel?: string | null;
}

export type Tranche =
  | "SANS_ECHEANCE"
  | "A_ECHOIR"
  | "ECHU_MOINS_DE_30"
  | "ECHU_31_A_60"
  | "ECHU_61_A_90"
  | "ECHU_PLUS_DE_90";

export interface PosteOuvertVue {
  piece: string;
  rang: number;
  numero: number | null;
  journal: string;
  date: string;
  echeance: string | null;
  libelle: string;
  reference: string | null;
  compte: string;
  tiers: string | null;
  sens: Sens;
  montant: number;
  /** Montant compté dans le sens de la dette : positif quand le tiers est créancier. */
  restantDu: number;
  joursDeRetard: number | null;
  tranche: Tranche;
}

export interface LigneAgeeVue {
  compte: string;
  tiers: string | null;
  nombreDePostes: number;
  total: number;
  echu: number;
  parTranche: Partial<Record<Tranche, number>>;
}

export interface TrancheVue {
  code: Tranche;
  libelle: string;
  estEchue: boolean;
  total: number;
}

export interface EcheancierVue {
  devise: string;
  dateDArrete: string;
  sensDeLaDette: Sens;
  postes: PosteOuvertVue[];
  lignes: LigneAgeeVue[];
  /** Tranches effectivement mouvementées, dans l'ordre de l'ancienneté. */
  tranches: TrancheVue[];
  total: number;
  totalEchu: number;
  totalAEchoir: number;
  postesSansEcheance: number;
}

export interface LigneDeFactureSaisie {
  compte: string;
  libelle: string | null;
  baseHT: number;
  codeDeTva: string | null;
  /** Taxe portée par la facture, lorsqu'elle corrige l'arrondi d'un centime. */
  tva: number | null;
}

export interface SaisieDeFacture {
  fournisseur: string;
  numero: string;
  date: string;
  lignes: LigneDeFactureSaisie[];
  libelle: string | null;
  echeance: string | null;
  journal: string | null;
  totalTtcAttendu: number | null;
}

export interface LigneVentileeVue {
  compte: string;
  libelle: string;
  baseHT: number;
  codeDeTva: string | null;
  tva: number;
  compteDeTva: string | null;
  totalTTC: number;
  ecartDArrondi: number;
}

export interface FactureDAchatVue {
  fournisseur: string;
  raisonSociale: string;
  numero: string;
  date: string;
  echeance: string;
  libelle: string;
  devise: string;
  lignes: LigneVentileeVue[];
  totalHT: number;
  totalTva: number;
  totalTTC: number;
  fournisseurIdentifie: boolean;
}

export interface AlerteDeDoublonVue {
  certitude: "CERTAIN" | "PROBABLE";
  certitudeLibelle: string;
  motif: string;
  piece: string;
  numeroDePiece: number | null;
  journal: string;
  statut: "BROUILLARD" | "VALIDEE";
  date: string;
  reference: string | null;
  montantTtc: number;
}

export interface ControleDeFactureVue {
  facture: FactureDAchatVue;
  alertes: AlerteDeDoublonVue[];
  doublonCertain: boolean;
  fournisseurSansIdentifiant: boolean;
}

export interface FactureEnregistreeVue {
  piece: PieceVue;
  facture: FactureDAchatVue;
  alertes: AlerteDeDoublonVue[];
}

export const fournisseursApi = {
  lister: (dossier: string, filtres: { recherche?: string; archivesInclus?: boolean } = {}) =>
    appeler<FournisseurVue[]>(avecParametres(`/dossiers/${dossier}/fournisseurs`, filtres)),

  consulter: (id: string) => appeler<FournisseurVue>(`/fournisseurs/${id}`),

  ouvrir: (dossier: string, saisie: SaisieDeFournisseur) =>
    envoyer<FournisseurVue>(`/dossiers/${dossier}/fournisseurs`, saisie),

  modifier: (id: string, saisie: SaisieDeFournisseur) =>
    appeler<FournisseurVue>(`/fournisseurs/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(saisie),
    }),

  archiver: (id: string) => envoyer<FournisseurVue>(`/fournisseurs/${id}/archivage`, {}),

  reactiver: (id: string) => envoyer<FournisseurVue>(`/fournisseurs/${id}/reactivation`, {}),

  options: () => appeler<Record<string, { code: string; libelle: string }[]>>("/fournisseurs-options"),

  /** Échéancier fournisseurs et balance âgée à une date (EF-0506). */
  echeancier: (dossier: string, filtres: { compte?: string; tiers?: string; au?: string } = {}) =>
    appeler<EcheancierVue>(avecParametres(`/dossiers/${dossier}/echeancier`, filtres)),
};

export const achatsApi = {
  /** Ventile et cherche les doublons sans rien enregistrer (EF-0504). */
  controler: (dossier: string, saisie: SaisieDeFacture) =>
    envoyer<ControleDeFactureVue>(`/dossiers/${dossier}/factures-achat/controle`, saisie),

  enregistrer: (
    dossier: string,
    saisie: SaisieDeFacture,
    options: { doublonAssume?: boolean; valider?: boolean } = {},
  ) =>
    envoyer<FactureEnregistreeVue>(
      avecParametres(`/dossiers/${dossier}/factures-achat`, {
        doublonAssume: options.doublonAssume,
        valider: options.valider,
      }),
      saisie,
    ),
};

// ---------------------------------------------------------------------------
// Import d'écritures (EF-0411)
// ---------------------------------------------------------------------------

export interface CorrespondanceVue {
  /** Rang de colonne de chaque champ, à partir de zéro. */
  colonnes: Partial<Record<ChampImporte, number>>;
  formatDeDate: string;
  avecEntete: boolean;
  journalParDefaut: string | null;
}

export interface CorrespondanceNommeeVue {
  nom: string;
  correspondance: CorrespondanceVue;
}

export interface ApercuVue {
  separateur: string;
  nombreDeLignes: number;
  nombreDeColonnes: number;
  lignes: string[][];
}

export interface PieceControleeVue {
  cle: string;
  journal: string;
  date: string;
  libelle: string;
  reference: string | null;
  lignes: number[];
  total: number | null;
  acceptee: boolean;
  motif: string | null;
  piece: string | null;
}

export interface RapportDImportVue {
  lignesLues: number;
  piecesAcceptees: number;
  piecesRejetees: number;
  lignesRejetees: number;
  enregistre: boolean;
  pieces: PieceControleeVue[];
  rejets: { ligne: number; motif: string }[];
}

export interface OptionsDImportVue {
  champs: { code: ChampImporte; libelle: string; obligatoire: boolean }[];
  formatsDeDate: { code: string; libelle: string }[];
}

export interface DemandeDImport {
  contenu: string;
  separateur: string | null;
  correspondance: CorrespondanceVue;
  fichier: string | null;
}

export const importApi = {
  options: () => appeler<OptionsDImportVue>("/imports/options"),

  apercu: (dossier: string, contenu: string, separateur: string | null) =>
    envoyer<ApercuVue>(`/dossiers/${dossier}/imports/apercu`, { contenu, separateur }),

  controler: (dossier: string, demande: DemandeDImport) =>
    envoyer<RapportDImportVue>(`/dossiers/${dossier}/imports/controle`, demande),

  importer: (dossier: string, demande: DemandeDImport) =>
    envoyer<RapportDImportVue>(`/dossiers/${dossier}/imports`, demande),

  correspondances: (dossier: string) =>
    appeler<CorrespondanceNommeeVue[]>(`/dossiers/${dossier}/imports/correspondances`),

  memoriser: (dossier: string, nom: string, correspondance: CorrespondanceVue) =>
    appeler<CorrespondanceNommeeVue>(`/dossiers/${dossier}/imports/correspondances`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ nom, correspondance }),
    }),

  oublier: (dossier: string, nom: string) =>
    appeler<void>(`/dossiers/${dossier}/imports/correspondances/${encodeURIComponent(nom)}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Déclarations de TVA (EF-1102, EF-1105)
// ---------------------------------------------------------------------------

export interface PeriodeVue {
  code: string;
  debut: string;
  fin: string;
}

export interface ResumeDeDeclarationVue {
  id: string;
  version: number;
  statut: "VALIDEE" | "DEPOSEE" | "REMPLACEE";
  statutLibelle: string;
  tvaDue: number;
  creditAReporter: number;
  valideeLe: string;
  deposeeLe: string | null;
}

export interface PeriodeDeclarativeVue {
  periode: PeriodeVue;
  declaration: ResumeDeDeclarationVue | null;
}

export interface LigneDeDeclarationVue {
  nature: "COLLECTEE" | "DEDUCTIBLE";
  compteDeTva: string;
  libelleDuCompte: string;
  code: string;
  taux: number;
  base: number;
  taxe: number;
  taxeCalculee: number;
  ecart: number;
}

export interface LigneDuReleveVue {
  piece: string;
  compteDeTva: string;
  reference: string | null;
  dateFacture: string;
  tiers: string | null;
  raisonSociale: string | null;
  identifiantFiscal: string | null;
  ice: string | null;
  designation: string;
  code: string;
  taux: number;
  base: number;
  taxe: number;
  ttc: number;
  modeDeReglement: string | null;
  dateDeReglement: string | null;
}

export interface AnomalieVue {
  nature: string;
  natureLibelle: string;
  piece: string | null;
  date: string | null;
  detail: string;
  montant: number | null;
}

export interface DeclarationVue {
  id: string | null;
  periode: PeriodeVue;
  regime: "ENCAISSEMENTS" | "DEBITS";
  version: number | null;
  statut: ResumeDeDeclarationVue["statut"] | null;
  statutLibelle: string | null;
  devise: string;
  chiffreDAffaires: number;
  tvaExigible: number;
  tvaDeductible: number;
  creditAnterieur: number;
  tvaDue: number;
  creditAReporter: number;
  lignes: LigneDeDeclarationVue[];
  releve: LigneDuReleveVue[];
  anomalies: AnomalieVue[];
  justification: string | null;
  valideeLe: string | null;
  deposeeLe: string | null;
  referenceDeDepot: string | null;
  remplace: string | null;
  courante: ResumeDeDeclarationVue | null;
}

export const declarationsApi = {
  echeancier: (dossier: string) => appeler<PeriodeDeclarativeVue[]>(`/dossiers/${dossier}/declarations-tva`),

  calculer: (dossier: string, date: string) =>
    appeler<DeclarationVue>(`/dossiers/${dossier}/declarations-tva/calcul?date=${date}`),

  valider: (dossier: string, date: string, justification: string | null) =>
    envoyer<DeclarationVue>(`/dossiers/${dossier}/declarations-tva/validation`, { date, justification }),

  consulter: (id: string) => appeler<DeclarationVue>(`/declarations-tva/${id}`),

  deposer: (id: string, reference: string | null) =>
    envoyer<DeclarationVue>(`/declarations-tva/${id}/depot`, { reference }),

  liquider: (id: string) => envoyer<PieceVue>(`/declarations-tva/${id}/liquidation`, {}),

  /** Adresse du relevé des déductions en CSV : un lien suffit, le cookie de session l'accompagne. */
  releveCsv: (id: string) => `/api/declarations-tva/${id}/releve.csv`,
};

// ---------------------------------------------------------------------------
// Banque et trésorerie (M07)
// ---------------------------------------------------------------------------

export interface CorrespondanceDeReleveVue {
  colonnes: Partial<Record<ChampDeReleve, number>>;
  formatDeDate: string;
  avecEntete: boolean;
}

export interface CompteBancaireVue {
  id: string;
  compte: string;
  journal: string;
  libelle: string;
  banque: string | null;
  iban: string | null;
  soldeInitial: number;
  dateDuSoldeInitial: string;
  correspondance: CorrespondanceDeReleveVue | null;
}

export interface EcritureDeBanqueVue {
  piece: string;
  rang: number;
  date: string;
  journal: string;
  libelle: string;
  reference: string | null;
  /** Au signe du relevé : positif pour un encaissement. */
  montant: number;
}

export interface PropositionVue {
  type: "POINTAGE" | "REGLEMENT" | "REGLE";
  motif: string;
  ecriture: EcritureDeBanqueVue | null;
  poste: {
    piece: string;
    rang: number;
    date: string;
    compte: string;
    tiers: string | null;
    reference: string | null;
    libelle: string;
    montant: number;
  } | null;
  regle: string | null;
}

export type StatutDOperation = "A_TRAITER" | "COMPTABILISEE" | "RAPPROCHEE" | "IGNOREE";

export interface OperationVue {
  id: string;
  date: string;
  dateDeValeur: string | null;
  libelle: string;
  reference: string | null;
  /** Positif pour un encaissement, négatif pour un décaissement. */
  montant: number;
  statut: StatutDOperation;
  statutLibelle: string;
  piece: string | null;
  note: string | null;
  propositions: PropositionVue[];
}

export interface RegleVue {
  id: string;
  nom: string;
  priorite: number;
  active: boolean;
  libelleContient: string | null;
  sens: "ENCAISSEMENT" | "DECAISSEMENT" | null;
  montantMinimal: number | null;
  montantMaximal: number | null;
  compteDeContrepartie: string;
  tiers: string | null;
  codeDeTva: string | null;
  libelleDEcriture: string | null;
  explication: string;
}

export type SaisieDeRegle = Omit<RegleVue, "id" | "explication">;

export interface EtatDeRapprochementVue {
  date: string;
  devise: string;
  soldeDuReleve: number;
  soldeComptable: number;
  ecart: number;
  totalNonComptabilise: number;
  totalNonPointe: number;
  ecartInexplique: number;
  estExplique: boolean;
  operationsNonComptabilisees: OperationVue[];
  ecrituresNonPointees: EcritureDeBanqueVue[];
}

export const banqueApi = {
  options: () =>
    appeler<{ champs: { code: ChampDeReleve; libelle: string; obligatoire: boolean }[]; formatsDeDate: { code: string; libelle: string }[] }>(
      "/banque/options",
    ),

  comptes: (dossier: string) => appeler<CompteBancaireVue[]>(`/dossiers/${dossier}/comptes-bancaires`),

  ouvrir: (
    dossier: string,
    compte: { compte: string; journal: string; libelle: string; banque: string | null; iban: string | null; soldeInitial: number; dateDuSoldeInitial: string },
  ) => envoyer<CompteBancaireVue>(`/dossiers/${dossier}/comptes-bancaires`, compte),

  importer: (compte: string, demande: { contenu: string; separateur: string | null; correspondance: CorrespondanceDeReleveVue; fichier: string | null }) =>
    envoyer<{ lignesLues: number; importees: number; doublons: number; rejets: { ligne: number; motif: string }[] }>(
      `/comptes-bancaires/${compte}/releves`,
      demande,
    ),

  operations: (compte: string, statut?: StatutDOperation) =>
    appeler<OperationVue[]>(`/comptes-bancaires/${compte}/operations${statut ? `?statut=${statut}` : ""}`),

  comptabiliser: (
    operation: string,
    traitement:
      | { regle: string }
      | { piece: string; rang: number }
      | { compte: string; tiers: string | null; libelle: string | null; codeDeTva: string | null },
  ) => envoyer<OperationVue>(`/operations-bancaires/${operation}/comptabilisation`, traitement),

  pointer: (operation: string, piece: string, rang: number) =>
    envoyer<OperationVue>(`/operations-bancaires/${operation}/pointage`, { piece, rang }),

  ignorer: (operation: string, motif: string) => envoyer<OperationVue>(`/operations-bancaires/${operation}/ignorer`, { motif }),

  rouvrir: (operation: string) => envoyer<OperationVue>(`/operations-bancaires/${operation}/reouverture`, {}),

  appliquer: (compte: string) => envoyer<{ traitees: number }>(`/comptes-bancaires/${compte}/propositions/application`, {}),

  rapprochement: (compte: string, date: string) =>
    appeler<EtatDeRapprochementVue>(`/comptes-bancaires/${compte}/rapprochement?date=${date}`),

  regles: (dossier: string) => appeler<RegleVue[]>(`/dossiers/${dossier}/regles-bancaires`),

  creerRegle: (dossier: string, regle: SaisieDeRegle) => envoyer<RegleVue>(`/dossiers/${dossier}/regles-bancaires`, regle),

  simulerRegle: (dossier: string, regle: SaisieDeRegle) =>
    envoyer<OperationVue[]>(`/dossiers/${dossier}/regles-bancaires/simulation`, regle),

  supprimerRegle: (regle: string) => appeler<void>(`/regles-bancaires/${regle}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Administration (EF-0104, EF-0109)
// ---------------------------------------------------------------------------

export interface RoleVue {
  id: string;
  code: string;
  libelle: string;
  standard: boolean;
  permissions: string[];
}

export interface HabilitationVue {
  id: string;
  role: string;
  roleLibelle: string;
  dossier: string | null;
  debut: string;
  fin: string | null;
  active: boolean;
}

export interface UtilisateurDuTenantVue {
  id: string;
  courriel: string;
  nom: string;
  statut: string;
  secondFacteurActif: boolean;
  habilitations: HabilitationVue[];
}

export interface TenantAdministreVue {
  id: string;
  raisonSociale: string;
  type: string;
  pays: string;
  statut: "ACTIF" | "SUSPENDU";
  quotaDeDossiers: number | null;
  nombreDUtilisateurs: number;
}

export const administrationApi = {
  utilisateurs: () => appeler<UtilisateurDuTenantVue[]>("/administration/utilisateurs"),

  roles: () => appeler<RoleVue[]>("/administration/roles"),

  habiliter: (demande: {
    courriel: string;
    nom: string;
    motDePasse: string | null;
    role: string;
    dossier: string | null;
    debut: string | null;
    fin: string | null;
  }) => envoyer<{ id: string }>("/administration/habilitations", demande),

  retirer: (habilitation: string) => appeler<void>(`/administration/habilitations/${habilitation}`, { method: "DELETE" }),

  reinitialiserLeSecondFacteur: (utilisateur: string) =>
    appeler<void>(`/administration/utilisateurs/${utilisateur}/second-facteur/reinitialisation`, { method: "POST" }),

  tenants: () => appeler<TenantAdministreVue[]>("/plateforme/tenants"),

  ouvrirTenant: (demande: {
    raisonSociale: string;
    type: string;
    pays: string;
    quotaDeDossiers: number | null;
    courrielDeLAdministrateur: string;
    nomDeLAdministrateur: string;
    motDePasseDeLAdministrateur: string | null;
  }) => envoyer<{ id: string }>("/plateforme/tenants", demande),

  changerLeStatut: (tenant: string, statut: "ACTIF" | "SUSPENDU") =>
    appeler<void>(`/plateforme/tenants/${tenant}/statut`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ statut }),
    }),

  changerLeQuota: (tenant: string, quotaDeDossiers: number | null) =>
    appeler<void>(`/plateforme/tenants/${tenant}/quota`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ quotaDeDossiers }),
    }),
};

// ---------------------------------------------------------------------------
// Pièces justificatives (EF-1505)
// ---------------------------------------------------------------------------

export interface JustificatifVue {
  id: string;
  nomDeFichier: string;
  typeDeMedia: string;
  taille: number;
  empreinte: string;
  deposeLe: string;
  /** Écritures que le justificatif justifie. */
  pieces: string[];
}

export const justificatifsApi = {
  dePiece: (piece: string) => appeler<JustificatifVue[]>(`/pieces/${piece}/justificatifs`),

  deposer: (piece: string, fichier: File) => {
    const formulaire = new FormData();
    formulaire.append("fichier", fichier);
    // Pas d'en-tête de type : le navigateur pose lui-même la frontière du
    // formulaire multipart.
    return appeler<{ justificatif: JustificatifVue; dejaDepose: boolean }>(`/pieces/${piece}/justificatifs`, {
      method: "POST",
      body: formulaire,
    });
  },

  detacher: (piece: string, justificatif: string) =>
    appeler<void>(`/pieces/${piece}/justificatifs/${justificatif}`, { method: "DELETE" }),

  /** Adresse du fichier, à ouvrir dans un onglet ou un cadre : le cookie de session l'accompagne. */
  contenu: (justificatif: string) => `/api/justificatifs/${justificatif}/contenu`,
};

// ---------------------------------------------------------------------------
// Piste d'audit (EF-1901, EF-1908)
// ---------------------------------------------------------------------------

export interface EvenementVue {
  id: string;
  horodatage: string;
  action: string;
  actionLibelle: string;
  utilisateur: string | null;
  auteur: string | null;
  entite: string | null;
  identifiantEntite: string | null;
  adresseIp: string | null;
  detail: Record<string, string>;
}

export const auditApi = {
  actions: () => appeler<{ code: string; libelle: string }[]>("/audit/actions"),

  rechercher: (filtre: { action?: string; dossier?: string; du?: string; au?: string; avant?: string; limite?: number }) =>
    appeler<EvenementVue[]>(avecParametres("/audit", filtre)),
};
